import { ref } from "vue"
import { defineStore } from "pinia"
import { api, getCookie, getSessionToken, getUser, authApi, setSessionVerified } from "@/compositions/utils.ts"

export interface User {
  user_id: number
  username: string
  email: string
  name: string
  user_level: string[]
  pms?: { id: number | string; [key: string]: any }[]
  news?: string
  csrf?: string
  [key: string]: any
}

export interface Vacation {
  user: string
  date: string
  [key: string]: any
}

// `impersonate` is `true` for an admin who isn't currently impersonating anyone,
// a username string while impersonating, and null when not applicable.
type ImpersonateState = boolean | string | null

const user = ref<Partial<User>>({})
const admin = ref<boolean | undefined>()
const pm = ref(false)
const translator = ref(false)
const developer = ref(false)
const programmer = ref(false)
const vacations = ref<Record<string, Vacation[]>>({})
const loggedIn = ref(false)
const impersonate = ref<ImpersonateState>(null)
const quotes = ref<any>(0)
// When a PM viewing /developer/:username clicks "Exit" inside NavigationDeveloper,
// the top bar reverts to NavigationAsync without leaving the page. Resets when the
// route name leaves the developer-view/request-view pair.
const showPMNavOverride = ref(false)

export const useUserStore = defineStore("UserStore", () => {
  async function fill(impersonating?: string) {
    if (impersonating) {
      const result = await authApi("startImpersonate", { username: impersonating })
      if (result?.error) return result.error
      const token = getSessionToken() || getCookie("BABBLE_SID")
      localStorage.setItem("babble/csrf", result.csrf)
      localStorage.setItem("babble_session", JSON.stringify({ token, data: result.user }))
      impersonate.value = impersonating
      setSessionVerified(false)
    } else if (impersonate.value) {
      const result = await authApi("stopImpersonate", {})
      if (result?.error) return result.error
      const token = getSessionToken() || getCookie("BABBLE_SID")
      localStorage.setItem("babble/csrf", result.csrf)
      localStorage.setItem("babble_session", JSON.stringify({ token, data: result.user }))
      impersonate.value = null
      setSessionVerified(false)
    }

    const phpID = getSessionToken() || getCookie("BABBLE_SID")
    const answer = await getUser(phpID)
    if (answer?.data && answer?.data.user_level) {
      loggedIn.value = true
      user.value = answer.data as User
      const u = user.value as User
      admin.value = u.user_level.includes("1")
      translator.value = u.user_level.includes("3")
      developer.value = u.user_level.includes("10")
      programmer.value = u.user_level.includes("101")
      pm.value = u.user_level.includes("4")
      if (admin.value === true) impersonate.value = impersonate.value || true
      else if (answer.impersonating) impersonate.value = u.username
      if (pm.value === true) localStorage.setItem("babble/pm", u.username)
      getVacations()
      return user.value
    } else {
      loggedIn.value = false
      localStorage.removeItem("babble/pm")
      return false
    }
  }

  async function getTimeClock(pm: string | undefined = user.value.username) {
    return await api("getTimeClock", "POST", { user: pm })
  }

  async function punchClock(pm: string | undefined = user.value.username, notes: string | null = null) {
    return await api("punchClock", "POST", { user: pm, notes: notes })
  }

  async function checkForQuotes() {
    quotes.value = await api("checkForQuotes", "GET")
  }

  async function getNotifications(user: string | null = localStorage.getItem("babble/pm")) {
    return await api("getNotifications", "POST", {
      user: user,
    })
  }

  async function getnews(user: string | null = localStorage.getItem("babble/pm")) {
    return await api("getNews", "POST", {
      user: user,
    })
  }

  async function getVacations() {
    const vacationslist: Record<string, Vacation[]> = {}
    const vacay: Vacation[] | undefined = await api("getVacations", "GET")
    if (vacay?.length) {
      vacay.forEach((v) => {
        if (!vacationslist[v.user]) vacationslist[v.user] = []
        vacationslist[v.user].push(v)
      })
    }
    vacations.value = vacationslist
  }

  async function getTimeClockHistory(user: string, startDate: string, endDate: string) {
    return await api("getTimeClockHistory", "POST", { user, startDate, endDate })
  }

  async function addTimeClockEntry(
    user: string,
    date: string | Date,
    time: string | number | Date | null,
    sick: number | null
  ) {
    return await api("addTimeClockEntry", "POST", { user, date, time, sick })
  }

  async function editTimeClockEntry(
    entryId: number | string,
    time?: string | number | Date | null,
    sick?: number | null
  ) {
    const params: Record<string, any> = { entryId }
    if (time !== undefined) params.time = time
    if (sick !== undefined) params.sick = sick
    return await api("editTimeClockEntry", "POST", params)
  }

  async function deleteTimeClockEntry(entryId: number | string) {
    return await api("deleteTimeClockEntry", "POST", { entryId })
  }

  async function getTimeClockRevenue(user: string, year: number | string, month: number | string) {
    return await api("getTimeClockRevenue", "POST", { user, year, month })
  }

  async function createTimeClockInvoice(
    user: string,
    year: number | string,
    month: number | string,
    hours: number,
    revshare: number
  ) {
    return await api("createTimeClockInvoice", "POST", { user, year, month, hours, revshare })
  }

  async function getTranslatorList(_devs?: boolean) {
    return await api("getTranslatorList", "GET")
  }

  /**
   * Set up session from auth API response (used by login and signup)
   */
  async function setSession(data: { sessionId: string; csrf: string; user: any }) {
    // Store CSRF token
    localStorage.setItem("babble/csrf", data.csrf)

    // Session cookie is now set server-side (httponly) by auth.php

    // Store session data in localStorage and mark verified so fill() skips redundant verifySession
    localStorage.setItem("babble_session", JSON.stringify({ token: data.sessionId, data: data.user }))
    setSessionVerified(true)

    // Fill the user store
    await fill()
  }

  async function logout() {
    try {
      const sessionId = getSessionToken() || getCookie("BABBLE_SID")
      if (sessionId) {
        await authApi("logout", { sessionId })
      }
    } catch (e) {
      console.error("Logout error:", e)
    }

    // Clear local state
    loggedIn.value = false
    user.value = {}
    admin.value = false
    pm.value = false
    translator.value = false
    developer.value = false
    quotes.value = 0
    impersonate.value = null
    setSessionVerified(false)

    // Clear all storage
    localStorage.removeItem("babble/pm")
    localStorage.removeItem("babble_session")
    localStorage.removeItem("babble/csrf")
    sessionStorage.clear()

    // Clear cookie with various domain/path combinations to ensure it's removed
    const domains = ["", window.location.hostname, ".ibabbleon.com", "ibabbleon.com", "www.ibabbleon.com"]
    const paths = ["/", "/dashboard", "/dashboard/"]
    for (const domain of domains) {
      for (const path of paths) {
        const domainPart = domain ? `; domain=${domain}` : ""
        document.cookie = `BABBLE_SID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domainPart}`
      }
    }

    return true
  }

  return {
    fill,
    setSession,
    logout,
    punchClock,
    getTimeClock,
    getTimeClockHistory,
    addTimeClockEntry,
    editTimeClockEntry,
    deleteTimeClockEntry,
    getTimeClockRevenue,
    createTimeClockInvoice,
    vacations,
    checkForQuotes,
    quotes,
    getNotifications,
    user,
    admin,
    pm,
    translator,
    developer,
    programmer,
    impersonate,
    loggedIn,
    showPMNavOverride,
    getTranslatorList,
    getnews,
  }
  // return { count, doubleCount, fill }
})
